import React from "react";
import AppContext from "../../contexts/AppContext";
import Creative from "../../photolab/Creative";
import processingManager from "../../photolab/ProcessingManager";
import Processing from "../../photolab/Processing";
import {
  PhotolabResponseError,
  PhotolabResponseParseError,
  photolabSimpleTask, photolabTask
} from "../../photolab/api";
import uploadHandler from "../../utils/upload.handler";
import {createCreativeByConfig, sortConfigsByWeights} from "../../photolab/config";
import ErrorView from "../../components/ErrorView";
import routes from "../../routes";
import {hitEvent, hits, logCreativeResult, logEvent, logProcessingsTimings, userEvents} from "../../utils/log";
import LoadingView from "./LoadingView/LoadingView";
import clientStorage from "../../utils/client-storage";
import {normalizeError, promisifyImage} from "../../photolab/handlers/helpers";
import PortraitModal from "./PortraitModal";
import PersonsModal from "./PersonsModal";
import {transformToDownloadUrl} from "../../utils/creative";
import {webviewAnalyticsEvent, webviewCall} from "../../utils/webview";
import AgeLimitModal from "./AgeLimitModal";
import {coverRectToBounds} from "../../utils/image";
import {bodyKeys, contextTypeKeys, extraKeys, genderKeys, stylizationKeys} from "../../photolab/etc";
import BadUploadPhotoModal from "./BadUploadPhotoModal";
import {signalEvent, signals} from "../../utils/signals";
import PhotoError from "../../photolab/PhotoError";
import i18n from "../../i18n";
import * as processingHelper from "../../helpers/processing.helper";
import * as photoHelper from "../../helpers/photo.helper";
import HairColorStyleModal from "./HairColorStyleModal";
import PhotolabTaskBuilder from "../../photolab/PhotolabTaskBuilder";
import PhotolabTaskCollageMethod from "../../photolab/PhotolabTaskCollageMethod";
import PhotolabTaskImageUrl from "../../photolab/PhotolabTaskImageUrl";
import {normalizeHairColor} from "../../photolab/handlers/d22sdxl.handler";
import {generatePath} from "react-router";

export default class ProcessingPage extends React.Component {

  state = {
    gender: null,
    contextType: null,
    error: null,
    imagesUrls: [],
    imageIsBlurred: false,
    isMultiface: false,
  };

  processingTimerId = null;
  processingTimer = 0;

  componentDidMount() {
    processingManager.clear();

    document.addEventListener("visibilitychange", this.startProcessingTimer, false);
    processingManager.addOnProcessingChangeHandler(this.handleProcessingChanged);

    const files = clientStorage.hasLatestSelectedImages()
      ? clientStorage.getLatestSelectedImages()
      : (this.props.location.state || {}).files;

    if (files) {
      this.handleUploadFiles(files);
    } else {
      this.props.history.replace(generatePath(routes.INDEX, {user_id: window.clientConfig.userId}) + "?reason=processing_nofiles");
    }
  }

  componentWillUnmount() {
    this.stopProcessingTimer();

    processingManager.removeOnProcessingChangeHandler(this.handleProcessingChanged);
  }

  stopProcessingTimer = () => {
    clearInterval(this.processingTimerId);
    document.removeEventListener("visibilitychange", this.startProcessingTimer, false);
  }

  startProcessingTimer = () => {
    clearInterval(this.processingTimerId);

    if (document.visibilityState !== "visible") {
      return;
    }

    this.processingTimerId = setInterval(() => {
      this.processingTimer++;

      if ((this.processingTimer * 1000) > window.appConfig.processings.timeout) {
        this.stopProcessingTimer();
        this.handleProcessingTimeout();
      }
    }, 1000);
  }

  handleProcessingChanged = () => {
    if (window.clientConfig.isDebug) {
      console.log("Processing changed", processingManager.processing);
    }

    const processing = processingManager.processing;

    const startedCreatives = processing.getStartedCreatives();
    const processedCreatives = startedCreatives.filter((c) => c.isProcessed && c.isSelected);
    const failedCreatives = startedCreatives.filter((c) => c.isFailed);
    const elapsedMs = Date.now() - processing.getExtra(Processing.EXTRA_STARTED_AT);

    if (processedCreatives.isNotEmpty()) {
      this.stopProcessingTimer();

      processingHelper.runOnceByExtra(processing, "processing_processed", () => {
        hitEvent(hits.PROCESSING_PROCESSED);
        signalEvent(signals.processingProcessed);
        logEvent(userEvents.PROCESSING_PROCESSED, {elapsed_time_ms: elapsedMs});
        logProcessingsTimings(elapsedMs);
      });

      this.props.history.replace(routes.RESULT);
    } else if (failedCreatives.length === startedCreatives.length) {
      this.stopProcessingTimer();

      const failedCreative = failedCreatives[0];

      if (failedCreative.error && failedCreative.error.type === "photolab") {
        hitEvent(hits.PROCESSING_FAILED_BY_PHOTOLAB);
        signalEvent(signals.processingFailedPhotolab);
      } else {
        hitEvent(hits.PROCESSING_FAILED);
        signalEvent(signals.processingFailed);
      }

      logEvent(userEvents.PROCESSING_FAILED, {elapsed_time_ms: elapsedMs});

      processingManager.clear();

      this.setState({
        error: failedCreative.error,
      });
    }
  };

  startProcessing = async (files) => {
    clientStorage.incrementSelectedPhotosAmount();

    let blockData = clientStorage.getLatestBlock();
    let section = this.context.config.sections.find((section) => section.name === blockData.section);
    if (!section) {
      this.props.history.replace(generatePath(routes.INDEX, {user_id: window.clientConfig.userId}) + "?reason=processing_nosection");
      return;
    }

    let block = section.blocks.find((block) => block.name === blockData.block);
    if (!block) {
      this.props.history.replace(generatePath(routes.INDEX, {user_id: window.clientConfig.userId}) + "?reason=processing_noblock");
      return;
    }

    try {
      const blockStylization = block.extra[extraKeys.stylization];
      const blockBody = block.extra[extraKeys.body];
      const blockContextType = block.extra[extraKeys.contextType];
      const blockSkipBodyCheck = block.extra[extraKeys.skipBodyCheck] || false;

      const skeletonTaskResult = await photolabSimpleTask(8800, files.first(), 500, 500);
      const file = {
        url: skeletonTaskResult.resultUrl,
      };

      if (!skeletonTaskResult.humans) {
        throw new PhotolabResponseError(-9999, "This effect requires a straight-on half or full body image. Please try again with another photo.");
      }

      const peopleAmountOnPhoto = skeletonTaskResult.humans.length;

      const questionsTaskConfig = new PhotolabTaskBuilder()
        .addMethod(new PhotolabTaskCollageMethod({"@configId": "hotify_main_questions"}))
        .addImage(new PhotolabTaskImageUrl(file.url))
        .buildToJs();

      const [sourceImage, proxiedSourceImage, questionsTaskResult] = await Promise.all([
        promisifyImage(file.url),
        promisifyImage(transformToDownloadUrl(file.url), true)
          .catch(() => { /*ignore*/}),
        photolabTask(questionsTaskConfig),
      ]);

      const answers = {
        age: questionsTaskResult.answers[0],
        is_child: questionsTaskResult.answers[1],
        gender: questionsTaskResult.answers[2],
      };

      const age = parseInt(answers.age);

      const genders = answers.gender.toLowerCase().split(" and ");

      const faces = photoHelper.extractFaces(sourceImage, skeletonTaskResult.humans, {
        paddingX: 0.4,
        paddingY: 0.25,
      });

      const croppedImagesUrls = proxiedSourceImage
        ? this.cropFaces(proxiedSourceImage, faces)
        : [file.url];

      const hasKids = age <= 20;
      const ageVerdict = !hasKids ? "age_ok"
        : (age < 13 && answers.is_child.toLowerCase() !== "no" ? "restriction_13" : "restriction_18");

      const bodiesStates = this.getBodyParts(sourceImage, faces);
      const bodiesIsValid = bodiesStates.map((item) => item["breast"]);
      const hasValidBody = bodiesIsValid.some((item) => item);

      const processing = new Processing();
      processing.setId(Date.now());
      processing.setLanguage(window.clientConfig.lang);
      processing.setFile(file, [0]);
      processing.setExtra(extraKeys.version, window.appConfig.processings.latestVersion);
      processing.setExtra(Processing.EXTRA_CREATED_AT, Date.now());
      processing.setExtra(Processing.EXTRA_CROPPED_IMAGES_URLS, croppedImagesUrls);
      processing.setExtra(Processing.EXTRA_RANDOM_GENDER, ["male", "female"].random());
      processing.setExtra("age_verdict", ageVerdict);
      processing.setExtra("bodies_states", bodiesStates);
      processing.setExtra("answers", answers);

      if (hasKids) {
        logCreativeResult(
          ageVerdict,
          [{url: processing.files.first().url}],
          [],
          false,
          {
            answers: processing.getExtra("answers"),
            age_verdict: ageVerdict,
            bodies_states: processing.getExtra("bodies_states"),
          }
        );

        const analyticsEventParams = [
          clientStorage.getSelectedPhotosAmount(),
          ageVerdict === "restriction_13" ? "under13yo" : "13-18yo",
          age,
        ];

        webviewAnalyticsEvent("age_alert_banner_shown", analyticsEventParams);

        await new Promise((resolve) => {
          this.context.pushModal(<AgeLimitModal
            key="ProcessingPage_AgeLimitModal"
            className="age-limit-modal"
            peopleAmountOnPhoto={peopleAmountOnPhoto}
            ageVerdict={ageVerdict}
            photoUrl={processing.files.first().url}
            onContinue={() => {
              webviewAnalyticsEvent("age_alert_button_tapped", [
                ...analyticsEventParams,
                "",
                "",
                "confirm",
              ]);

              resolve();
            }}
            onFileChooseButtonClick={() => {
              signalEvent(signals.photoSelectClick);
              webviewAnalyticsEvent("age_alert_button_tapped", [
                ...analyticsEventParams,
                "",
                "",
                "choose another photo",
              ]);
            }}
            onFilesSelected={this.handleFilesSelected}
          />);
        });
      }

      webviewAnalyticsEvent("photo_uploaded", [
        clientStorage.getSelectedPhotosAmount(),
        bodiesStates.first() && bodiesStates.first().breast ? "yes" : "no",
        bodiesIsValid.first() ? "yes" : "no",
        peopleAmountOnPhoto,
        age,
      ], {
        wt_hotify: {
          breast_point_2: bodiesStates.last() && bodiesStates.last().breast ? "yes" : "no",
          torso_point_2: bodiesIsValid.last() ? "yes" : "no",
          age_limits_2: age,
          block_cnt: clientStorage.getSelectedBlocksAmount(),
        },
      });

      if (!blockSkipBodyCheck) {
        if (!hasValidBody) {
          logCreativeResult(
            "no_breast",
            [{url: processing.files.first().url}],
            [],
            false,
            {
              answers: processing.getExtra("answers"),
              age_verdict: processing.getExtra("age_verdict"),
              bodies_states: processing.getExtra("bodies_states"),
            }
          );

          webviewAnalyticsEvent("change_photo_banner_shown", [
            clientStorage.getSelectedPhotosAmount(),
            bodiesStates.first().breast ? "yes" : "no",
          ]);

          if (window.clientConfig.isPro || !window.clientConfig.features.shouldShowPhotoGuideModal) {
            await new Promise((resolve) => {
              this.context.pushModal(<PortraitModal
                key="ProcessingPage_PortraitModal"
                className="bad-upload-photo-modal portrait-modal"
                photoUrl={processing.files.first().url}
                onContinue={resolve}
                onFilesSelected={this.handleFilesSelected}
              />);
            })
          } else {
            await new Promise((resolve) => {
              this.context.pushModal(<BadUploadPhotoModal
                key="ProcessingPage_BadUploadPhotoModal"
                className="bad-upload-photo-modal"
                photoUrl={processing.files.first().url}
                onSubscribe={() => {
                  // const params = {
                  //   placement: "pro_web",
                  //   onClose: "bannerOnClose",
                  //   a: "a",
                  //   b: "b"
                  // };
                  //
                  // webviewCall("banner", params);
                  processingManager.clear();
                  this.props.history.replace(generatePath(routes.INDEX, {user_id: window.clientConfig.userId}) + "?reason=processing_bad_photo_upload");
                }}

                onContinue={() => {
                  webviewAnalyticsEvent("proceed_anyway_button_tapped", [
                    clientStorage.getSelectedPhotosAmount(),
                  ]);

                  resolve();
                }}
                onFileChooseButtonClick={() => {
                  signalEvent(signals.photoSelectClick);
                  webviewAnalyticsEvent("change_photo_button_tapped", [
                    clientStorage.getSelectedPhotosAmount(),
                  ]);
                }}
                onFilesSelected={this.handleFilesSelected}
              />);
            });
          }
        }
      }

      // --
      let blockCreatives = JSON.parse(JSON.stringify(block.creatives));
      let allCreatives = JSON.parse(JSON.stringify(section.blocks.flatMap((block) => block.creatives)));

      let personPhotoWarning = null;

      if (blockCreatives.length > 0) {
        if (blockCreatives.filter((config) => config.people === peopleAmountOnPhoto).isEmpty()) {
          const people = blockCreatives.first().people;
          personPhotoWarning = people === 1 ? "one_person_photo_warning" : "two_person_photo_warning";
        }
      } else {
        if (allCreatives.filter((config) => config.people === peopleAmountOnPhoto).isEmpty()) {
          const people = allCreatives.first().people;
          personPhotoWarning = people === 1 ? "one_person_photo_warning" : "two_person_photo_warning";
        }
      }

      if (personPhotoWarning === "one_person_photo_warning") {
        await new Promise((resolve) => {
          this.context.pushModal(<PersonsModal
            key="ProcessingPage_PersonsModal"
            className="bad-upload-photo-modal portrait-modal persons-modal"
            title={i18n.t("persons_modal_1__title")}
            text={i18n.t("persons_modal_1__text")}
            badText={i18n.t("persons_modal_1__bad_text_modal")}
            goodText={i18n.t("persons_modal_1__good_text_modal")}
            goodExamples={"/assets/images/goodExamples1.png"}
            photoUrl={processing.files.first().url}
            onFilesSelected={this.handleFilesSelected}
            onContinue={() => {
              if (!window.clientConfig.isPro) {
                // const params = {
                //   placement: "pro_web",
                //   onClose: "bannerOnClose",
                //   a: "a",
                //   b: "b"
                // };
                // webviewCall("banner", params);
                this.handleCancel();
              }
              
              ({section, block} = this.switchBlock(2));
              blockData = clientStorage.getLatestBlock();
              blockCreatives = JSON.parse(JSON.stringify(block.creatives));
              allCreatives = JSON.parse(JSON.stringify(section.blocks.flatMap((block) => block.creatives)));

              resolve();
            }}
          />);
        });
      } else if (personPhotoWarning === "two_person_photo_warning") {
        await new Promise((resolve) => {
          this.context.pushModal(<PersonsModal
            key="ProcessingPage_PersonsModal"
            className="bad-upload-photo-modal portrait-modal persons-modal"
            title={i18n.t("persons_modal_2__title")}
            text={i18n.t("persons_modal_2__text")}
            badText={i18n.t("persons_modal_2__bad_text_modal")}
            goodText={i18n.t("persons_modal_2__good_text_modal")}
            goodExamples={"/assets/images/goodExamples2.png"}
            photoUrl={processing.files.first().url}
            onFilesSelected={this.handleFilesSelected}
            onContinue={() => {
              // if (!window.clientConfig.isPro) {
              //   const params = {
              //     placement: "pro_web",
              //     onClose: "bannerOnClose",
              //     a: "a",
              //     b: "b"
              //   };
              //   webviewCall("banner", params);
              //   this.handleCancel();
              // }

              ({section, block} = this.switchBlock(1));
              blockData = clientStorage.getLatestBlock();
              blockCreatives = JSON.parse(JSON.stringify(block.creatives));
              allCreatives = JSON.parse(JSON.stringify(section.blocks.flatMap((block) => block.creatives)));

              resolve();
            }}
          />);
        });
      }

      processing.setExtra(extraKeys.sectionName, blockData.section);
      processing.setExtra(extraKeys.sectionPosition, blockData.section_position);
      processing.setExtra(extraKeys.blockName, blockData.block);
      processing.setExtra(extraKeys.blockPosition, blockData.block_position);

      processing.setExtra(
        extraKeys.stylization,
        Object.values(stylizationKeys).includes(blockStylization)
          ? blockStylization
          : stylizationKeys.glamour
      );

      processing.setExtra(
        extraKeys.body,
        Object.values(bodyKeys).includes(blockBody)
          ? blockBody
          : bodyKeys.slim
      );

      processing.setExtra(
        extraKeys.contextType,
        Object.values(contextTypeKeys).includes(blockContextType)
          ? blockContextType
          : contextTypeKeys.template
      );
      // \---

      blockCreatives.forEach((creative) => {
        creative.extra = creative.extra || {};
        creative.extra.weight = creative.extra.weight || Math.random();
      });

      allCreatives.forEach((creative) => {
        creative.extra = creative.extra || {};
        creative.extra.weight = creative.extra.weight || Math.random();
      });

      const creatives = [
        ...sortConfigsByWeights(blockCreatives),
        ...sortConfigsByWeights(allCreatives),
      ].filter((creative, index, arr) => {
        return arr.findIndex((c2) => c2.name === creative.name) === index;
      });

      // --

      if (peopleAmountOnPhoto === 1) {
        const gender = genders[0];

        const configs = creatives.filter((c) => c.people === 1);
        let configsForMale = configs.filter((c) => c.genders.indexOf(genderKeys.male) > -1);
        let configsForFemale = configs.filter((c) => c.genders.indexOf(genderKeys.female) > -1);
        const configsHasSdxl = configs.some((c) => c.type === "d22sdxl");

        if (block.extra && block.extra.generate_seeds) {
          const chosenName = block.creatives.first().name.substring(2, 12);
          configsForMale = [0,1,2,3,4,5,6,7,8,9].map((item) => configsForMale.find((c) => c.name.includes(chosenName)) || configsForMale.random());
          configsForFemale = [0,1,2,3,4,5,6,7,8,9].map((item) => configsForFemale.find((c) => c.name.includes(chosenName)) || configsForFemale.random());
        }

        processing.setExtra(Processing.EXTRA_CONFIGS_ORDER_MALE, configsForMale.map((p) => p.name));
        processing.setExtra(Processing.EXTRA_CONFIGS_ORDER_FEMALE, configsForFemale.map((p) => p.name));
        processing.setExtra(Processing.EXTRA_CONFIGS, configs);
        processing.setExtra(extraKeys.gender, gender);
        processing.setExtra("generateSeeds", block.extra && block.extra.generate_seeds);

        if (configsHasSdxl) {
          const cropV15TaskConfig = new PhotolabTaskBuilder()
            .addMethod(new PhotolabTaskCollageMethod({template_name: 6637}))
            .addImage(new PhotolabTaskImageUrl(file.url, "", 0, 0))
            .buildToJs();

          const cropV15TaskResult = await photolabTask(cropV15TaskConfig);
          processing.setTask("crop_v15", cropV15TaskResult);

          const sdxlQuestionsTaskConfig = new PhotolabTaskBuilder()
            .setLanguage(processing.language)
            .addMethod(new PhotolabTaskCollageMethod({
              template_name: "nn_blip",
              questions: "[|What gender is this person?|,|What hair color this person has?|,|What color of human skin this person has?|]"
            }))
            .addImage(new PhotolabTaskImageUrl(cropV15TaskResult.resultUrl, "", 0, 0))
            .buildToJs();

          const sdxlQuestionsTaskResult = await photolabTask(sdxlQuestionsTaskConfig);
          processing.setTask("d22sdxl_questions", sdxlQuestionsTaskResult);

          await new Promise((resolve, reject) => {
            this.context.pushModal(<HairColorStyleModal
              key="ProcessingPage_HairColorStyleModal"
              className="hair-style-modal"
              gender={gender}
              hairColor={normalizeHairColor(sdxlQuestionsTaskResult.answers[1])}
              onColorSelect={(color, style) => {
                processing.setExtra(extraKeys.selectedHairColor, color);
                processing.setExtra(extraKeys.selectedHairStyle, style);
                resolve();
              }}
            />);
          });
        }
      }
      else if (peopleAmountOnPhoto === 2) {
        const gender = genders.length === 1
          ? [genders[0], genders[0]].join(" and ")
          : genders.join(" and ");

        const configs = creatives.filter((c) => c.people === 2);

        processing.setExtra(Processing.EXTRA_CONFIGS_ORDER, configs.map((p) => p.name));
        processing.setExtra(Processing.EXTRA_CONFIGS, configs);
        processing.setExtra(Processing.EXTRA_IS_MULTIFACE, true);
        processing.setExtra(extraKeys.gender, gender);
      }
      else {
        throw new PhotoError("There is a maximum limit of 2 people in a photo for this effect to work.");
      }

      const creative = createCreativeByConfig(
        processing,
        processing.getItemByPosition(0, processing.getExtra(extraKeys.gender)),
        {
          gender: processing.getExtra(extraKeys.gender),
          stylization: processing.getExtra(extraKeys.stylization),
          body: processing.getExtra(extraKeys.body),
          contextType: processing.getExtra(extraKeys.contextType),
        },
      );

      // if (!window.clientConfig.isPro && blockData.isPro) {
      //   const params = {
      //     placement: "pro_web",
      //     onClose: "bannerOnClose",
      //     a: "a",
      //     b: "b"
      //   };
      //
      //   webviewCall("banner", params);
      //   processingManager.clear();
      //   this.props.history.replace(generatePath(routes.INDEX, {user_id: window.clientConfig.userId}) + "?reason=processing_bad_photo_upload");
      // }

      creative.setExtra(Creative.EXTRA_POSITION, 0);
      creative.removeExtra(Creative.EXTRA_KEEP_PENDING);
      creative.setAsSelected(true);

      processing.addCreative(creative);

      processingManager.start(processing);
      
      signalEvent(signals.processingStarted);

      this.setState({
        imagesUrls: croppedImagesUrls,
        imageIsBlurred: false,
        isMultiface: peopleAmountOnPhoto > 1,
        gender: processing.getExtra(extraKeys.gender),
      });

      this.startProcessingTimer();
    } catch (err) {
      console.error(err);

      processingManager.clear();

      webviewAnalyticsEvent("photo_upload_error", [
        clientStorage.getSelectedPhotosAmount(),
        err.code,
        err.message,
      ]);

      const isPhotolabError = err instanceof PhotolabResponseError || err instanceof PhotolabResponseParseError;

      if (!isPhotolabError) {
        logEvent(userEvents.PROCESSING_ERROR, {
          message: err.message,
          code: err.code,
        });
      }

      this.setState({
        gender: null,
        contextType: null,
        imagesUrls: [],
        imageIsBlurred: false,
        isMultiface: false,
        error: normalizeError(err),
      });
    }
  }

  switchBlock = (peopleAmount) => {
    const section = this.context.config.sections
      .find((s) => s.blocks.find((b) => b.creatives.find((c) => c.people === peopleAmount)));
    const block = section.blocks.find((b) => b.creatives.find((c) => c.people === peopleAmount));

    clientStorage.setLatestBlock({
      config: {
        id: this.context.config.id,
        version: this.context.config.version,
      },
      section: section.name,
      section_position: this.context.config.sections.findIndex((s) => s.name === section.name),
      block: block.name,
      block_position: section.blocks.findIndex((b) => b.name === block.name),
      people: peopleAmount,
    });

    clientStorage.incrementSelectedBlocksAmount();
    webviewAnalyticsEvent("block_selected", [
      clientStorage.getSelectedBlocksAmount(),
      section.name,
      block.name,
    ]);

    return {section, block}
  }

  getBodyParts = (sourceImage, faces) => {
    return faces.map((face) => {
      return {
        breast: sourceImage.height > (face.realRect.y2 + (face.realRect.y2 - face.realRect.y1) * 0.7),
      }
    });
  }

  cropFaces = (sourceImage, faces) => {
    return faces.map((face) => {
      const sourceRect = {
        x: face.extendedRect.x1,
        y: face.extendedRect.y1,
        width: face.extendedRect.x2 - face.extendedRect.x1,
        height: face.extendedRect.y2 - face.extendedRect.y1,
      }

      const canvas = document.createElement("canvas");
      canvas.width = 166;
      canvas.height = 166;

      const destRect = coverRectToBounds({width: sourceRect.width, height: sourceRect.height}, canvas);

      canvas.getContext("2d").drawImage(
        sourceImage,
        sourceRect.x, sourceRect.y, sourceRect.width, sourceRect.height,
        destRect.x, destRect.y, destRect.width, destRect.height
      );

      return canvas.toDataURL("image/jpeg", 65);
    });
  }

  handleProcessingTimeout = () => {
    processingManager.clear();

    this.setState({
      gender: null,
      contextType: null,
      imagesUrls: [],
      imageIsBlurred: false,
      isMultiface: false,
      error: {
        type: "processing_timeout",
        code: 1,
        message: "timeout",
      },
    });
  };

  handleFilesSelected = (files) => {
    this.setState({
      gender: null,
      contextType: null,
      imagesUrls: [],
      imageIsBlurred: false,
      isMultiface: false,
      error: null,
    }, () => {
      processingManager.clear();
      this.handleUploadFiles(files);
    });
  }

  handleUploadFiles = (files) => {
    if (!(files instanceof FileList)) {
      this.handleFilesUploaded(files);
      return;
    }

    Promise.all([...files].map((image) => uploadHandler(image)))
      .then(this.handleFilesUploaded)
      .catch((error) => {
        this.setState({error});
      });
  }

  handleFilesUploaded = (files) => {
    this.setState({
      imagesUrls: [files.first().url],
      imageIsBlurred: true,
    });
    this.startProcessing(files);
  }

  handleCancel = () => {
    processingManager.clear();
    this.props.history.replace(generatePath(routes.INDEX, {user_id: window.clientConfig.userId}) + "?reason=processing_cancel");
  }

  handleGenderChanged = (nextGender) => {
    this.setState({gender: nextGender});

    const processing = processingManager.processing;
    if (!processing) {
      return;
    }

    const config = processing.getItemByPosition(0, nextGender);
    const currentCreative = processing.getSelectedCreativeByPosition(0);

    let nextCreative = processing.getCreativeByParams(
      0,
      nextGender,
      currentCreative.getExtra(extraKeys.contextType),
      currentCreative.getExtra(extraKeys.body),
      currentCreative.getExtra(extraKeys.stylization)
    );

    if (!nextCreative) {
      nextCreative = createCreativeByConfig(
        processing,
        config,
        {
          gender: nextGender,
          stylization: currentCreative.getExtra(extraKeys.stylization),
          body: currentCreative.getExtra(extraKeys.body),
          contextType: currentCreative.getExtra(extraKeys.contextType),
        }
      );

      nextCreative.setExtra(Creative.EXTRA_POSITION, 0);
      processing.addCreative(nextCreative);
    }

    nextCreative.setAsSelected(true);
    currentCreative.setAsSelected(false);

    processing.setExtra(extraKeys.gender, nextGender);
    processingManager.update();
  }

  handleContextTypeChanged = (nextContextType) => {
    this.setState({contextType: nextContextType});

    const processing = processingManager.processing;
    if (!processing) {
      return;
    }

    const config = processing.getItemByPosition(0);
    const currentCreative = processing.getSelectedCreativeByPosition(0);

    const nextContextTypeIsOriginal = nextContextType === contextTypeKeys.original;

    let nextCreative = processing.getCreativeByParams(
      0,
      currentCreative.getExtra(extraKeys.gender),
      nextContextType,
      currentCreative.getExtra(extraKeys.body),
      currentCreative.getExtra(extraKeys.stylization)
    );

    if (!nextCreative) {
      nextCreative = createCreativeByConfig(
        processing,
        config,
        {
          gender: currentCreative.getExtra(extraKeys.gender),
          stylization: currentCreative.getExtra(extraKeys.stylization),
          body: nextContextTypeIsOriginal ? bodyKeys.slim : currentCreative.getExtra(extraKeys.body),
          contextType: nextContextType
        }
      );

      nextCreative.setExtra(Creative.EXTRA_POSITION, 0);
      processing.addCreative(nextCreative);
    }

    currentCreative.setAsSelected(false);
    nextCreative.setAsSelected(true);

    processing.setExtra(extraKeys.contextType, nextContextType);

    processingManager.update();
  }

  handleRestartButtonClick = () => {
    processingManager.clear();

    const redirectUrl = new URL(window.location.href);
    redirectUrl.pathname = "/";
    redirectUrl.search = "";

    if (window.clientConfig.isWebview) {
      webviewCall("close", {});

      Object.keys(window.clientConfig.webviewParams).forEach((key) => {
        redirectUrl.searchParams.append(key, window.clientConfig.webviewParams[key]);
      });
    }

    redirectUrl.searchParams.append("r", Math.round(100000 * Math.random()));

    window.location.href = redirectUrl.toString();
  };

  handleRetryButtonClick = () => {
    const locationState = this.props.location.state || {};
    const files = clientStorage.hasLatestSelectedImages()
      ? clientStorage.getLatestSelectedImages()
      : locationState.files;

    this.handleFilesSelected(files);
  }

  render() {
    if (this.state.error) {
      return <ErrorView
        error={this.state.error}
        onFilesSelected={this.handleFilesSelected}
        onRestartClick={this.handleRestartButtonClick}
        onRetryClick={this.handleRetryButtonClick}
      />;
    }

    return <LoadingView
      imagesUrls={this.state.imagesUrls}
      imageIsBlurred={this.state.imageIsBlurred}
      isMultiface={this.state.isMultiface}
      gender={this.state.gender}
      onGenderChanged={this.handleGenderChanged}
      onContextTypeChanged={this.handleContextTypeChanged}
      onCancel={this.handleCancel}
    />;
  }
}

ProcessingPage.contextType = AppContext;
